import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, setDoc, getDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeString, sanitizeEmail } from '../../utils/sanitization';
import { useAuth } from '../auth/AuthContext';
import { useGamification } from '../gamification/GamificationContext';
import { API_URL } from '../../utils/apiConfig';
import { generateTimestampId } from '../../utils/documentIdUtils';
import { logError, logUserAction } from '../../utils/logger';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';
import { usePermissions } from '../../hooks/usePermissions';
import { evaluateAllSegments } from '../../utils/segmentEvaluation';
import { findUnknownVariables, formatUnknownVariablesMessage } from '../../utils/emailTemplateVariables';
import { canViewAllEmail, scopeEmailAudience } from '../../utils/emailAccess';

const CAMPAIGN_TABS = [
  { id: 'details', label: 'Details', icon: '📝' },
  { id: 'template', label: 'Template', icon: '🎨' },
  { id: 'audience', label: 'Audience', icon: '👥' },
  { id: 'schedule', label: 'Schedule', icon: '📅' }
];

const CampaignBuilder = ({ campaign, onClose, onSaved }) => {
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const viewAllEmail = canViewAllEmail(currentUser, can);
  const { recordActivity } = useGamification();
  
  const DEFAULT_DOMAINS = ['sofibellawear.com'];
  const [verifiedDomains, setVerifiedDomains] = useState(DEFAULT_DOMAINS);

  // Load verified sender domains from system settings
  useEffect(() => {
    const loadDomains = async () => {
      try {
        const snap = await getDoc(doc(db, collectionPath('systemSettings'), 'verifiedSenderDomains'));
        if (snap.exists()) {
          const domains = snap.data().jsonValue;
          if (Array.isArray(domains) && domains.length > 0) {
            setVerifiedDomains(domains);
          }
        }
      } catch (e) {
        console.warn('Failed to load verified sender domains:', e);
      }
    };
    loadDomains();
  }, []);

  const [formState, setFormState] = useState({
    name: '',
    description: '',
    fromName: 'Sofibella Activewear',
    fromEmail: 'customerservice@sofibellawear.com',
    replyTo: 'customerservice@sofibellawear.com',
    templateId: '',
    segmentIds: [],
    scheduledAt: null,
    isPrivate: false,
    ...campaign
  });

  // Parse fromEmail into prefix and domain for the structured selector
  const [fromEmailPrefix, setFromEmailPrefix] = useState(() => {
    const email = campaign?.fromEmail || 'customerservice@sofibellawear.com';
    return email.split('@')[0] || 'customerservice';
  });
  const [fromEmailDomain, setFromEmailDomain] = useState(() => {
    const email = campaign?.fromEmail || 'customerservice@sofibellawear.com';
    return email.split('@')[1] || 'sofibellawear.com';
  });

  // When verifiedDomains loads, validate the current domain selection
  useEffect(() => {
    if (verifiedDomains.length > 0 && !verifiedDomains.includes(fromEmailDomain)) {
      setFromEmailDomain(verifiedDomains[0]);
    }
  }, [verifiedDomains]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details'); // details, template, audience, schedule
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showRecipientPreview, setShowRecipientPreview] = useState(false);
  const [audienceData, setAudienceData] = useState(null); // { contacts, customers, opportunities }
  const [loadingAudience, setLoadingAudience] = useState(false);
  const [dynamicSegmentCounts, setDynamicSegmentCounts] = useState({}); // Per-segment counts

  // Load templates and segments
  useEffect(() => {
    let active = true;
    setTemplates([]);
    setSegments([]);
    const loadData = async () => {
      try {
        // Load templates
        const templatesSnap = await getDocs(
          query(collection(db, collectionPath('emailTemplates')))
        );
        const templatesData = templatesSnap.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        })).filter(t => !t.isArchived && (!t.isPrivate || t.createdBy === currentUser.uid || viewAllEmail));
        if (!active) return;
        setTemplates(templatesData);

        // Load segments
        const segmentsSnap = await getDocs(
          query(collection(db, collectionPath('emailSegments')),
            ...(!viewAllEmail ? [where('createdBy', '==', currentUser.uid)] : []))
        );
        const segmentsData = segmentsSnap.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        })).filter(s => !s.isArchived);
        if (!active) return;
        setSegments(segmentsData);
      } catch (err) {
        await logError(err, {
          action: 'LOAD_CAMPAIGN_DATA',
          entityType: 'Campaign'
        });
        console.error('Failed to load data:', {
          action: 'LOAD_CAMPAIGN_DATA',
          error: err.message,
          timestamp: new Date().toISOString()
        });
        if (active) setError('Failed to load templates and segments');
      }
    };

    loadData();
    return () => { active = false; };
  }, [currentUser.uid, viewAllEmail]);

  // Load audience data (contacts, customers, opportunities) for dynamic evaluation
  // Loads as soon as any segment is selected, or for sent campaigns
  const isSentCampaign = campaign?.status === 'sent' || (campaign?.status === 'sending' && !campaign?.error);
  const shouldLoadAudience = formState.segmentIds.length > 0 || isSentCampaign;
  useEffect(() => {
    if (!shouldLoadAudience) return;
    let active = true;
    const loadAudienceData = async () => {
      setAudienceData(null);
      setLoadingAudience(true);
      try {
        const [contactsSnap, customersSnap, oppsSnap, orderSummaryCacheDoc] = await Promise.all([
          getDocs(query(collection(db, collectionPath('contacts')))),
          getDocs(query(collection(db, collectionPath('customers')))),
          getDocs(query(collection(db, collectionPath('opportunities')))),
          getDoc(doc(db, collectionPath('reportsCache'), 'customer_order_summary'))
        ]);
        let customersData = customersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Enrich customers with order summary cache (same as SegmentBuilder)
        const orderSummary = orderSummaryCacheDoc.exists() ? (orderSummaryCacheDoc.data().customers || {}) : {};
        customersData = customersData.map(customer => {
          const acct = String(customer.customerAccountNumber || customer.id);
          const summary = orderSummary[acct];
          return {
            ...customer,
            lastOrderDate: customer.lastOrderDate || (summary?.lastOrderDate ?? null),
            totalRevenue: customer.totalRevenue ?? (summary?.totalRevenue || 0),
            totalOrders: customer.totalOrders ?? (summary?.totalOrders || 0)
          };
        });
        if (!active) return;
        setAudienceData(scopeEmailAudience({
          contacts: contactsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
          customers: customersData,
          opportunities: oppsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        }, currentUser, viewAllEmail));
      } catch (err) {
        console.error('Failed to load audience data:', err);
        if (active) setError(`Could not load audience: ${err.message}`);
      } finally {
        if (active) setLoadingAudience(false);
      }
    };
    loadAudienceData();
    return () => { active = false; };
  }, [shouldLoadAudience, currentUser, viewAllEmail]);

  // Dynamically evaluate segment recipients whenever segments change or data loads
  useEffect(() => {
    if (formState.segmentIds.length === 0) {
      setRecipientCount(0);
      setPreviewRecipients([]);
      setDynamicSegmentCounts({});
      return;
    }
    // If audience data not loaded yet, use stored contactCount as fallback
    if (!audienceData) {
      if (segments.length === 0) return;
      const selected = segments.filter(s => formState.segmentIds.includes(s.id));
      setRecipientCount(selected.reduce((sum, s) => sum + (s.contactCount || 0), 0));
      return;
    }
    const selected = segments.filter(s => formState.segmentIds.includes(s.id));
    if (selected.length === 0) return;
    const { recipients, perSegmentCounts, totalCount } = evaluateAllSegments(selected, audienceData);
    setDynamicSegmentCounts(perSegmentCounts);
    setRecipientCount(totalCount);
    setPreviewRecipients(recipients.slice(0, 20));
  }, [formState.segmentIds, segments, audienceData]);

  // Load template preview
  useEffect(() => {
    if (formState.templateId) {
      const template = templates.find(t => t.id === formState.templateId);
      setPreviewTemplate(template || null);
    } else {
      setPreviewTemplate(null);
    }
  }, [formState.templateId, templates]);

  const handleInputChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Sync prefix+domain to fromEmail whenever either changes
  useEffect(() => {
    const cleanPrefix = fromEmailPrefix.replace(/[^a-zA-Z0-9._+-]/g, '').toLowerCase();
    if (cleanPrefix) {
      const composed = `${cleanPrefix}@${fromEmailDomain}`;
      setFormState(prev => prev.fromEmail !== composed ? { ...prev, fromEmail: composed } : prev);
    }
  }, [fromEmailPrefix, fromEmailDomain]);

  const handleSegmentToggle = (segmentId) => {
    setFormState(prev => {
      const segmentIds = prev.segmentIds.includes(segmentId)
        ? prev.segmentIds.filter(id => id !== segmentId)
        : [...prev.segmentIds, segmentId];
      return { ...prev, segmentIds };
    });
    setShowRecipientPreview(false);
  };

  const validateForm = () => {
    if (!formState.name?.trim()) {
      setError('Campaign name is required');
      return false;
    }
    if (!formState.templateId) {
      setError('Please select a template');
      return false;
    }
    if (formState.segmentIds.length === 0) {
      setError('Please select at least one audience segment');
      return false;
    }
    if (!formState.fromEmail || !validateEmail(formState.fromEmail)) {
      setError('Valid sender email is required');
      return false;
    }
    if (formState.replyTo && !validateEmail(formState.replyTo)) {
      setError('Reply-to email is invalid');
      return false;
    }
    return true;
  };

  // Unresolved merge tags are stripped at send time, so catch typos before delivery
  const validateTemplateVariables = () => {
    const template = templates.find(t => t.id === formState.templateId);
    if (!template) return true;
    const unknown = findUnknownVariables(template.subject, template.htmlContent, template.textContent);
    if (unknown.length > 0) {
      setError(`Template "${template.name}" has invalid merge variables. ${formatUnknownVariablesMessage(unknown)}`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError('');

    try {
      // Generate human-readable ID first (for new campaigns)
      let campaignId = campaign?.id;
      
      if (!campaignId) {
        const namePart = sanitizeString(formState.name, 50)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 40);
        campaignId = generateTimestampId(`campaign_${namePart}`);
      }

      const sanitized = {
        name: sanitizeString(formState.name, 200),
        description: sanitizeString(formState.description, 500),
        fromName: sanitizeString(formState.fromName, 100),
        fromEmail: sanitizeEmail(formState.fromEmail),
        replyTo: sanitizeEmail(formState.replyTo),
        templateId: formState.templateId,
        segmentIds: formState.segmentIds,
        scheduledAt: formState.scheduledAt,
        status: formState.status || 'draft',
        isPrivate: Boolean(formState.isPrivate),
        lastEditedBy: currentUser?.uid || '',
        lastEditedByName: currentUser?.displayName || currentUser?.email || 'Unknown',
        updatedAt: serverTimestamp()
      };

      if (campaign?.id) {
        // Update existing campaign
        // Assign createdBy if missing (legacy campaigns)
        if (!campaign.createdBy) {
          sanitized.createdBy = currentUser?.uid || '';
          sanitized.createdByName = currentUser?.displayName || currentUser?.email || 'Unknown';
        }
        
        // Store recipient count if not already sent
        if (campaign.status !== 'sent') {
          sanitized.recipientCount = recipientCount;
        }
        
        await updateDoc(
          doc(db, collectionPath('emailCampaigns'), campaign.id),
          sanitized
        );
        await logUserAction({ action: 'Email Campaign Updated', details: `Campaign updated: "${formState.name}"`, entityType: 'EmailCampaign', entityId: campaign.id });
        onSaved('Campaign updated successfully');
      } else {
        // Create new campaign with the generated ID
        
        await setDoc(
          doc(db, collectionPath('emailCampaigns'), campaignId),
          {
            ...sanitized,
            recipientCount,
            createdBy: currentUser?.uid || '',
            createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
            stats: {
              sent: 0,
              delivered: 0,
              opens: 0,
              uniqueOpens: 0,
              clicks: 0,
              uniqueClicks: 0,
              bounces: 0,
              unsubscribes: 0
            },
            createdAt: serverTimestamp()
          }
        );
        
        console.log('Campaign created with ID:', {
          action: 'CREATE_CAMPAIGN',
          campaignId,
          recipientCount,
          status: 'draft',
          timestamp: new Date().toISOString()
        });
        await logUserAction({ action: 'Email Campaign Created', details: `Campaign created: "${formState.name}" (${recipientCount} recipients)`, entityType: 'EmailCampaign' });
        onSaved('Campaign created successfully');
      }
    } catch (err) {
      await logError(err, {
        action: 'SAVE_CAMPAIGN',
        entityType: 'Campaign',
        campaignId: campaign?.id || 'new'
      });
      console.error('Save error:', {
        action: 'SAVE_CAMPAIGN',
        error: err.message,
        code: err.code,
        campaignId: campaign?.id || 'new',
        timestamp: new Date().toISOString()
      });
      setError('Failed to save campaign: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!validateForm()) return;
    if (!validateTemplateVariables()) return;

    const confirmed = window.confirm(
      `Send this campaign to ${recipientCount} recipients now?\n\n` +
      `This action cannot be undone.`
    );
    
    if (!confirmed) return;

    setSending(true);
    setError('');

    try {
      // Generate human-readable ID first (for new campaigns)
      let campaignId = campaign?.id;
      
      if (!campaignId) {
        const namePart = sanitizeString(formState.name, 50)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 40);
        campaignId = generateTimestampId(`campaign_${namePart}`);
      }
      
      // Prepare recipients data (compact version for storage)
      if (!campaign?.id) {
        // Create new campaign with the generated ID
        
        const sanitized = {
          name: sanitizeString(formState.name, 200),
          description: sanitizeString(formState.description, 500),
          fromName: sanitizeString(formState.fromName, 100),
          fromEmail: sanitizeEmail(formState.fromEmail),
          replyTo: sanitizeEmail(formState.replyTo),
          templateId: formState.templateId,
          segmentIds: formState.segmentIds,
          recipientCount,
          status: 'sending',
          error: null,
          stats: {
            sent: 0,
            delivered: 0,
            opens: 0,
            uniqueOpens: 0,
            clicks: 0,
            uniqueClicks: 0,
            bounces: 0,
            unsubscribes: 0
          },
          createdBy: currentUser?.uid || '',
          createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await setDoc(
          doc(db, collectionPath('emailCampaigns'), campaignId),
          sanitized
        );
      } else {
        // Update existing campaign before sending
        await updateDoc(
          doc(db, collectionPath('emailCampaigns'), campaignId),
          {
            name: sanitizeString(formState.name, 200),
            fromName: sanitizeString(formState.fromName, 100),
            fromEmail: sanitizeEmail(formState.fromEmail),
            replyTo: sanitizeEmail(formState.replyTo),
            templateId: formState.templateId,
            segmentIds: formState.segmentIds,
            recipientCount,
            status: 'sending',
            error: null,
            lastEditedBy: currentUser?.uid || '',
            lastEditedByName: currentUser?.displayName || currentUser?.email || 'Unknown',
            updatedAt: serverTimestamp()
          }
        );
      }

      // Call server API to send emails
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/campaigns/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ campaignId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to send campaign';
        
        // Update campaign with error status
        await updateDoc(
          doc(db, collectionPath('emailCampaigns'), campaignId),
          {
            status: 'failed',
            error: errorMessage,
            updatedAt: serverTimestamp()
          }
        );
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Don't set status to 'sent' here — the server-side sendCampaignBatch
      // will set the final status (sent/partial_failure/failed) after actually
      // sending the emails. The campaign is currently 'sending' which is correct.
      // Just clear any previous error.
      await updateDoc(
        doc(db, collectionPath('emailCampaigns'), campaignId),
        {
          error: null,
          updatedAt: serverTimestamp()
        }
      );
      
      // Gamification: Award points for sending campaign (with anti-abuse validation)
      try { 
        await recordActivity('CAMPAIGN_SENT', {
          entityId: campaignId,
          source: 'manual'
        }); 
      } catch (e) { console.warn('Gamification failed:', e); }

      onSaved(`Campaign is sending to ${recipientCount} recipients. Check back shortly for delivery results.`);
      await logUserAction({ action: 'Email Campaign Sent', details: `Campaign "${formState.name}" sent to ${recipientCount} recipients`, entityType: 'EmailCampaign', entityId: campaign?.id });
    } catch (err) {
      await logError(err, {
        action: 'SEND_CAMPAIGN',
        entityType: 'Campaign',
        campaignId: campaign?.id || 'new',
        recipientCount
      });
      console.error('Send error:', {
        action: 'SEND_CAMPAIGN',
        error: err.message,
        code: err.code,
        campaignId: campaign?.id || 'new',
        timestamp: new Date().toISOString()
      });
      setError('Failed to send campaign: ' + err.message);
      setSending(false);
      // Don't close modal on error so user can fix issues
      return;
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!validateForm()) return;
    if (!validateTemplateVariables()) return;
    if (!formState.scheduledAt) {
      setError('Please select a schedule date and time');
      return;
    }

    const scheduledDate = new Date(formState.scheduledAt);
    if (scheduledDate <= new Date()) {
      setError('Scheduled time must be in the future');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Generate human-readable ID first (for new campaigns)
      let campaignId = campaign?.id;
      
      if (!campaignId) {
        const namePart = sanitizeString(formState.name, 50)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 40);
        campaignId = generateTimestampId(`campaign_${namePart}`);
      }

      const sanitized = {
        name: sanitizeString(formState.name, 200),
        description: sanitizeString(formState.description, 500),
        fromName: sanitizeString(formState.fromName, 100),
        fromEmail: sanitizeEmail(formState.fromEmail),
        replyTo: sanitizeEmail(formState.replyTo),
        templateId: formState.templateId,
        segmentIds: formState.segmentIds,
        scheduledAt: Timestamp.fromDate(new Date(formState.scheduledAt)),
        status: 'scheduled',
        error: null,
        updatedAt: serverTimestamp()
      };

      if (campaign?.id) {
        await updateDoc(
          doc(db, collectionPath('emailCampaigns'), campaign.id),
          sanitized
        );
      } else {
        // Create new campaign with the generated ID
        
        await setDoc(
          doc(db, collectionPath('emailCampaigns'), campaignId),
          {
            ...sanitized,
            createdBy: currentUser?.uid || '',
            createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
            stats: {
              sent: 0,
              delivered: 0,
              opens: 0,
              uniqueOpens: 0,
              clicks: 0,
              uniqueClicks: 0,
              bounces: 0,
              unsubscribes: 0
            },
            createdAt: serverTimestamp()
          }
        );
      }

      onSaved(`Campaign scheduled for ${scheduledDate.toLocaleString()}`);
      await logUserAction({ action: 'Email Campaign Scheduled', details: `Campaign "${formState.name}" scheduled for ${scheduledDate.toLocaleString()}`, entityType: 'EmailCampaign', entityId: campaign?.id });
    } catch (err) {
      await logError(err, {
        action: 'SCHEDULE_CAMPAIGN',
        entityType: 'Campaign',
        campaignId: campaign?.id || 'new',
        scheduledAt: formState.scheduledAt
      });
      console.error('Schedule error:', {
        action: 'SCHEDULE_CAMPAIGN',
        error: err.message,
        code: err.code,
        campaignId: campaign?.id || 'new',
        timestamp: new Date().toISOString()
      });
      setError('Failed to schedule campaign: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderDetailsTab = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Campaign Name *
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          value={formState.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="e.g., Spring 2025 Collection Launch"
          maxLength={200}
        />
        <p className="text-xs text-gray-500 mt-1">
          Internal name for this campaign (not visible to recipients)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From Name *
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            value={formState.fromName}
            onChange={(e) => handleInputChange('fromName', e.target.value)}
            maxLength={100}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From Email *
          </label>
          <div className="flex">
            <input
              type="text"
              className="flex-1 min-w-0 border border-gray-300 rounded-l px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              value={fromEmailPrefix}
              onChange={(e) => {
                setFromEmailPrefix(e.target.value.replace(/[^a-zA-Z0-9._+-]/g, '').toLowerCase());
                setError('');
              }}
              placeholder="customerservice"
            />
            <span className="inline-flex items-center px-2 bg-gray-100 border-y border-gray-300 text-gray-500 text-sm">@</span>
            <select
              className="border border-gray-300 rounded-r px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-white"
              value={fromEmailDomain}
              onChange={(e) => {
                setFromEmailDomain(e.target.value);
                setError('');
              }}
            >
              {verifiedDomains.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Only verified SendGrid domains are available
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reply-To Email
        </label>
        <input
          type="email"
          className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          value={formState.replyTo}
          onChange={(e) => handleInputChange('replyTo', e.target.value)}
          placeholder="Optional - defaults to From Email"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Campaign Description
        </label>
        <textarea
          className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          value={formState.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Describe the purpose and goals of this campaign..."
          rows={3}
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1">
          Optional: Explain the intent and goals of this campaign for your team (not visible to recipients)
        </p>
      </div>

      {/* Privacy Setting */}
      <div className="border-t pt-4">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formState.isPrivate}
            onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
            className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">Private Campaign</div>
            <p className="text-xs text-gray-500 mt-1">
              When enabled, only you can see and edit this campaign. Other users won't see it in their campaign list.
            </p>
          </div>
        </label>
      </div>
    </div>
  );

  const renderTemplateTab = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Email Template *
        </label>
        <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto">
          {templates.length === 0 ? (
            <div className="col-span-2 text-center py-8 text-gray-500">
              No templates available. Create a template first.
            </div>
          ) : (
            templates.map(template => (
              <div
                key={template.id}
                onClick={() => handleInputChange('templateId', template.id)}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  formState.templateId === template.id
                    ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                    : 'border-gray-300 hover:border-indigo-400'
                }`}
              >
                {template.thumbnailUrl && (
                  <img
                    src={template.thumbnailUrl}
                    alt={template.name}
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                )}
                <h4 className="font-medium text-gray-900">{template.name}</h4>
                <p className="text-xs text-gray-500 mt-1">{template.category}</p>
                {template.variables && template.variables.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Variables: {template.variables.join(', ')}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {previewTemplate && (
        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-700 mb-2">Template Preview</h4>
          <div className="border rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
            <div className="text-sm mb-2">
              <strong>Subject:</strong> {previewTemplate.subject}
            </div>
            {previewTemplate.htmlContent && (
              <iframe
                srcDoc={previewTemplate.htmlContent}
                className="w-full h-48 border rounded"
                title="Template Preview"
                sandbox="allow-same-origin allow-scripts"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderAudienceTab = () => (
    <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Audience Segments *
          </label>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {segments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No segments available. Create a segment first.
              </div>
            ) : (
              segments.map(segment => (
                <label
                  key={segment.id}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                    formState.segmentIds.includes(segment.id)
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formState.segmentIds.includes(segment.id)}
                    onChange={() => handleSegmentToggle(segment.id)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-grow">
                    <div className="font-medium text-gray-900">{segment.name}</div>
                    <div className="text-sm text-gray-600">{segment.description}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {(dynamicSegmentCounts[segment.id] ?? segment.contactCount ?? 0).toLocaleString()} recipients
                      <span className="text-gray-400 ml-1">(with valid emails){audienceData && dynamicSegmentCounts[segment.id] !== undefined ? '' : ' · estimated'}</span>
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

      {formState.segmentIds.length > 0 && (
        <div className="border-t pt-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            {loadingAudience ? (
              <div className="text-center py-4 text-indigo-600">Evaluating audience...</div>
            ) : (
            <>
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="font-medium text-indigo-900">
                  Total Recipients: {recipientCount.toLocaleString()}
                </span>
                <p className="text-xs text-indigo-600 mt-1">
                  (Valid email addresses from selected segments{!audienceData ? ' · loading live data...' : ''})
                </p>
              </div>
              {audienceData && (
              <button
                type="button"
                onClick={() => setShowRecipientPreview(prev => !prev)}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                {showRecipientPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
              )}
            </div>
            
            {showRecipientPreview && (
              <div className="mt-3">
                {previewRecipients.length > 0 ? (
                  <>
                    <p className="text-sm text-indigo-700 mb-2">
                      Preview (showing first {previewRecipients.length} of {recipientCount.toLocaleString()}):
                    </p>
                <div className="bg-white rounded border border-indigo-200 max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-indigo-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-indigo-700 uppercase">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-indigo-700 uppercase">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-indigo-700 uppercase">Customer</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-indigo-100">
                      {previewRecipients.map((recipient, idx) => (
                        <tr key={idx} className="hover:bg-indigo-50">
                          <td className="px-3 py-2 text-indigo-900">
                            {recipient.contactName || recipient.variables?.customerName || 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-indigo-600">
                            {recipient.contactEmail || recipient.email}
                          </td>
                          <td className="px-3 py-2 text-indigo-700">
                            {recipient.customer?.customerName || recipient.variables?.customerName || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    No recipients with valid email addresses found in selected segments.
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderScheduleTab = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Send Options
        </label>
        <div className="space-y-3">
          <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="sendOption"
              checked={!formState.scheduledAt}
              onChange={() => handleInputChange('scheduledAt', null)}
              className="mr-3"
            />
            <div>
              <div className="font-medium text-gray-900">Send Immediately</div>
              <div className="text-sm text-gray-600">
                Campaign will be sent as soon as you click "Send Campaign"
              </div>
            </div>
          </label>

          <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="sendOption"
              checked={!!formState.scheduledAt}
              onChange={() => {
                // Set default to tomorrow at 9 AM
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(9, 0, 0, 0);
                handleInputChange('scheduledAt', tomorrow.toISOString().slice(0, 16));
              }}
              className="mt-1 mr-3"
            />
            <div className="flex-grow">
              <div className="font-medium text-gray-900 mb-2">Schedule for Later</div>
              {formState.scheduledAt && (
                <div className="space-y-1">
                  <input
                    type="datetime-local"
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    value={formState.scheduledAt}
                    onChange={(e) => handleInputChange('scheduledAt', e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                  <p className="text-xs text-gray-500">
                    Your timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </p>
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      {formState.scheduledAt && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Scheduled for:</strong> {new Date(formState.scheduledAt).toLocaleString()}
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            The campaign will be sent automatically at this time. You can cancel or reschedule anytime before then.
          </p>
        </div>
      )}
    </div>
  );

  const isReadOnly = !can(FEATURE_PERMISSIONS.EMAIL_EDIT_CAMPAIGN) || campaign?.status === 'sent' || campaign?.status === 'partial_failure' || campaign?.status === 'failed' || (campaign?.status === 'sending' && !campaign?.error);

  // Render read-only summary view for sent campaigns
  const renderSentCampaignSummary = () => {
    const template = templates.find(t => t.id === formState.templateId);
    const campaignSegments = (formState.segmentIds || []).map(id => segments.find(s => s.id === id)).filter(Boolean);
    const sentRecipientCount = campaign?.stats?.sent || 0;
    const failedCount = campaign?.stats?.failed || 0;
    const totalRecipients = campaign?.recipientCount || 0;

    // Determine banner style based on campaign outcome
    const isTotalFailure = campaign?.status === 'failed';
    const isPartialFailure = campaign?.status === 'partial_failure';
    const isSuccess = !isTotalFailure && !isPartialFailure;

    const bannerConfig = isTotalFailure
      ? { bg: 'bg-red-50 border-red-200', icon: '❌', title: 'Campaign Failed to Send', titleColor: 'text-red-900', labelColor: 'text-red-700', valueColor: 'text-red-900' }
      : isPartialFailure
      ? { bg: 'bg-yellow-50 border-yellow-200', icon: '⚠️', title: 'Campaign Partially Sent', titleColor: 'text-yellow-900', labelColor: 'text-yellow-700', valueColor: 'text-yellow-900' }
      : { bg: 'bg-green-50 border-green-200', icon: '✅', title: 'Campaign Sent Successfully', titleColor: 'text-green-900', labelColor: 'text-green-700', valueColor: 'text-green-900' };

    return (
      <div className="space-y-6">
        {/* Campaign Status Banner */}
        <div className={`${bannerConfig.bg} border rounded-lg p-4`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{bannerConfig.icon}</span>
            <h4 className={`text-lg font-semibold ${bannerConfig.titleColor}`}>{bannerConfig.title}</h4>
          </div>
          {/* Error message for failures */}
          {campaign?.error && (
            <div className={`text-sm ${isTotalFailure ? 'text-red-800' : 'text-yellow-800'} mb-3 p-2 rounded ${isTotalFailure ? 'bg-red-100' : 'bg-yellow-100'}`}>
              {campaign.error}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className={`${bannerConfig.labelColor} font-medium`}>Created:</span>
              <span className={`ml-2 ${bannerConfig.valueColor}`}>
                {campaign?.createdAt ? new Date(campaign.createdAt.toDate()).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div>
              <span className={`${bannerConfig.labelColor} font-medium`}>{isTotalFailure ? 'Attempted:' : 'Sent:'}</span>
              <span className={`ml-2 ${bannerConfig.valueColor}`}>
                {campaign?.sentAt ? new Date(campaign.sentAt.toDate()).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div>
              <span className={`${bannerConfig.labelColor} font-medium`}>Delivered:</span>
              <span className={`ml-2 ${bannerConfig.valueColor}`}>
                {sentRecipientCount} of {totalRecipients}
              </span>
            </div>
            {failedCount > 0 && (
              <div>
                <span className="text-red-700 font-medium">Failed:</span>
                <span className="ml-2 text-red-900 font-semibold">{failedCount}</span>
              </div>
            )}
          </div>
          {/* Failure details for troubleshooting */}
          {campaign?.failureDetails && (
            <details className="mt-3">
              <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">Show failure details</summary>
              <div className="mt-2 text-xs bg-white/50 rounded p-2 space-y-1">
                {Object.entries(campaign.failureDetails).map(([reason, count]) => (
                  <div key={reason} className="flex justify-between">
                    <span className="text-gray-700 truncate mr-2">{reason}</span>
                    <span className="text-red-600 font-medium whitespace-nowrap">{count} emails</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Campaign Details */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Campaign Details</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase">Campaign Name</label>
                <div className="text-gray-900 font-medium">{formState.name || 'Untitled'}</div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">From Name</label>
                <div className="text-gray-900">{formState.fromName}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <label className="text-xs text-gray-500 uppercase">From Email</label>
                <div className="text-gray-900">{formState.fromEmail}</div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Reply-To Email</label>
                <div className="text-gray-900">{formState.replyTo}</div>
              </div>
            </div>
            {formState.description && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Campaign Description</label>
                <div className="text-gray-900 text-sm">{formState.description}</div>
              </div>
            )}
            {/* Reply-To now shown in its own column above */}
          </div>
        </div>

        {/* Audience Segments */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Audience Segments</h4>
          {campaignSegments.length > 0 ? (
            <div className="space-y-2">
              {campaignSegments.map(segment => (
                <div key={segment.id} className="bg-white border border-gray-200 rounded p-3">
                  <div className="font-medium text-gray-900">{segment.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{segment.description}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {segment.contactCount?.toLocaleString() || 0} contacts in segment
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500">No segment information available</div>
          )}
        </div>

        {/* Template Used */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Email Template</h4>
          {template ? (
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                {template.thumbnailUrl && (
                  <img
                    src={template.thumbnailUrl}
                    alt={template.name}
                    className="w-24 h-24 object-cover rounded border border-gray-300"
                  />
                )}
                <div className="flex-grow">
                  <div className="font-medium text-gray-900">{template.name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    <strong>Subject:</strong> {template.subject}
                  </div>
                  {template.category && (
                    <div className="text-xs text-gray-500 mt-1">
                      Category: {template.category}
                    </div>
                  )}
                </div>
              </div>

              {/* Email Preview */}
              <div className="border-t pt-3">
                <button
                  type="button"
                  onClick={() => setPreviewTemplate(previewTemplate ? null : template)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  {previewTemplate ? '▼ Hide Email Preview' : '▶ Show Email Preview'}
                </button>
                
                {previewTemplate && template.htmlContent && (
                  <div className="mt-3 border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-3 py-2 text-sm text-gray-700 border-b">
                      Email Preview
                    </div>
                    <iframe
                      srcDoc={template.htmlContent}
                      className="w-full h-96 border-0"
                      title="Email Template Preview"
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-gray-500">No template information available</div>
          )}
        </div>

        {/* Recipients */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Recipients</h4>
            <div className="text-sm">
              <span className="font-medium text-gray-900">{sentRecipientCount.toLocaleString()}</span>
              <span className="text-gray-600 ml-1">emails sent</span>
            </div>
          </div>
          
          {sentRecipientCount > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowRecipientPreview(!showRecipientPreview)}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium mb-3"
              >
                {showRecipientPreview ? '▼ Hide Recipients List' : '▶ Show Recipients List'}
              </button>
              
              {showRecipientPreview && (
                loadingAudience ? (
                  <div className="text-center py-4 text-indigo-600">Loading recipients...</div>
                ) : previewRecipients.length > 0 ? (
                <div className="bg-white rounded border border-gray-300 max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Customer</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {previewRecipients.map((recipient, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">
                            {recipient.contactName || 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {recipient.contactEmail}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {recipient.customer?.customerName || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    No recipients data available. Segments may have changed since this campaign was sent.
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">
            {isReadOnly ? 'Campaign Summary' : campaign?.id ? 'Edit Campaign' : 'Create Campaign'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Tabs (only for editable campaigns) */}
        {!isReadOnly && (
          <div className="border-b px-6 flex space-x-4">
            {CAMPAIGN_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-grow overflow-y-auto px-6 py-4">
          {campaign?.error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <div className="font-semibold mb-1">⚠️ Previous Send Failed</div>
              <div className="text-sm">{campaign.error}</div>
              <div className="text-xs mt-2 text-red-600">
                The campaign status has been reset. Please review and try sending again.
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {isReadOnly ? (
            renderSentCampaignSummary()
          ) : (
            <>
              {activeTab === 'details' && renderDetailsTab()}
              {activeTab === 'template' && renderTemplateTab()}
              {activeTab === 'audience' && renderAudienceTab()}
              {activeTab === 'schedule' && renderScheduleTab()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            {!isReadOnly && recipientCount > 0 && (
              <span className="text-sm text-gray-600">
                Will send to {recipientCount.toLocaleString()} recipients
              </span>
            )}
          </div>
          
          <div className="flex space-x-2">
            {!isReadOnly && activeTab !== CAMPAIGN_TABS[0].id && (
              <button
                onClick={() => {
                  const idx = CAMPAIGN_TABS.findIndex(t => t.id === activeTab);
                  if (idx > 0) setActiveTab(CAMPAIGN_TABS[idx - 1].id);
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                ← Previous
              </button>
            )}
            
            {!isReadOnly && (
              <button
                onClick={handleSave}
                disabled={saving || sending}
                className="px-4 py-2 border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save as Draft'}
              </button>
            )}
            
            {!isReadOnly && activeTab !== CAMPAIGN_TABS[CAMPAIGN_TABS.length - 1].id && (
              <button
                onClick={() => {
                  const idx = CAMPAIGN_TABS.findIndex(t => t.id === activeTab);
                  if (idx < CAMPAIGN_TABS.length - 1) setActiveTab(CAMPAIGN_TABS[idx + 1].id);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Next →
              </button>
            )}
            
            {!isReadOnly && activeTab === CAMPAIGN_TABS[CAMPAIGN_TABS.length - 1].id && (
              formState.scheduledAt ? (
                <button
                  onClick={handleSchedule}
                  disabled={saving || sending}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Scheduling...' : 'Schedule Campaign'}
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={saving || sending}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Send Campaign'}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to validate email
function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

// Helper function to apply contact segment rules (enriches contacts with customer data)
function applyContactSegmentRules(contacts, customers, rules) {
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return contacts;
  }

  // Create customer lookup map for O(1) access
  const customersById = new Map(customers.map(c => [c.id, c]));

  // Enrich contacts with customer data
  const enrichedContacts = contacts.map(contact => ({
    ...contact,
    customer: customersById.get(contact.customerId) || {}
  }));

  // Filter enriched contacts using rules
  return enrichedContacts.filter(contact => {
    const results = rules.conditions.map(condition => {
      const fieldValue = getNestedValue(contact, condition.field);

      switch (condition.operator) {
        case 'equals':
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() === condition.value.toLowerCase();
          }
          return fieldValue === condition.value;
        case 'not_equals':
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() !== condition.value.toLowerCase();
          }
          return fieldValue !== condition.value;
        case 'contains':
          if (Array.isArray(fieldValue)) {
            // Special handling for salesReps array
            if (condition.field === 'customer.salesReps') {
              return fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            return fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'not_contains':
          if (Array.isArray(fieldValue)) {
            if (condition.field === 'customer.salesReps') {
              return !fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            return !fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return !String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'after':
          return new Date(fieldValue) > new Date(condition.value);
        case 'before':
          return new Date(fieldValue) < new Date(condition.value);
        case 'greater_than':
          return Number(fieldValue) > Number(condition.value);
        case 'less_than':
          return Number(fieldValue) < Number(condition.value);
        case 'is_empty':
          return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
        case 'is_not_empty':
          return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0);
        default:
          return false;
      }
    });

    if (rules.operator === 'OR') {
      return results.some(r => r);
    } else {
      return results.every(r => r);
    }
  });
}

// Helper function to apply segment rules (simplified client-side version)
function applySegmentRules(customers, rules) {
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return customers;
  }

  return customers.filter(customer => {
    const results = rules.conditions.map(condition => {
      const fieldValue = getNestedValue(customer, condition.field);

      switch (condition.operator) {
        case 'equals':
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() === condition.value.toLowerCase();
          }
          return fieldValue === condition.value;
        case 'not_equals':
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() !== condition.value.toLowerCase();
          }
          return fieldValue !== condition.value;
        case 'contains':
          if (Array.isArray(fieldValue)) {
            return fieldValue.includes(condition.value);
          }
          return String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'not_contains':
          if (Array.isArray(fieldValue)) {
            return !fieldValue.includes(condition.value);
          }
          return !String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'after':
          return new Date(fieldValue) > new Date(condition.value);
        case 'before':
          return new Date(fieldValue) < new Date(condition.value);
        case 'greater_than':
          return Number(fieldValue) > Number(condition.value);
        case 'less_than':
          return Number(fieldValue) < Number(condition.value);
        case 'is_empty':
          return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
        case 'is_not_empty':
          return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0);
        default:
          return false;
      }
    });

    if (rules.operator === 'OR') {
      return results.some(r => r);
    } else {
      return results.every(r => r);
    }
  });
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

export default CampaignBuilder;
